import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Plus, Trash2, ArrowRightLeft, TrendingUp, RefreshCw, AlertTriangle, Wallet } from 'lucide-react';

export const AdminTreasuryPage = () => {
  const [overview, setOverview] = useState(null);
  const [rules, setRules] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [poolForm, setPoolForm] = useState(null);
  const [ruleForm, setRuleForm] = useState(null);
  const [transferForm, setTransferForm] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [o, r, l] = await Promise.all([
        adminAPI.getTreasuryOverview(),
        adminAPI.listRevenueRules(),
        adminAPI.getTreasuryLedger({ limit: 100 }),
      ]);
      setOverview(o.data);
      setRules(r.data);
      setLedger(l.data);
    } catch (e) { toast.error('Failed to load treasury'); }
  };

  const savePool = async () => {
    try {
      if (poolForm.id) {
        await adminAPI.updateTreasuryPool(poolForm.id, { name: poolForm.name, description: poolForm.description, enabled: poolForm.enabled });
      } else {
        await adminAPI.createTreasuryPool({ key: poolForm.key, name: poolForm.name, description: poolForm.description });
      }
      toast.success('Saved');
      setPoolForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const deletePool = async (p) => {
    if (!window.confirm(`Delete pool "${p.name}"? It must be empty and non-protected.`)) return;
    try {
      await adminAPI.deleteTreasuryPool(p.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const doTransfer = async () => {
    try {
      const res = await adminAPI.transferBetweenPools({
        from_pool: transferForm.from_pool, to_pool: transferForm.to_pool,
        currency: transferForm.currency, amount: parseFloat(transferForm.amount),
        reason: transferForm.reason,
      });
      toast.success(res.data.message);
      setTransferForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const saveRule = async () => {
    try {
      const totalPct = (ruleForm.splits || []).reduce((sum, s) => sum + (parseFloat(s.percent) || 0), 0);
      if (totalPct > 100) return toast.error(`Splits sum to ${totalPct}%, cannot exceed 100`);
      const payload = {
        label: ruleForm.label,
        enabled: ruleForm.enabled,
        splits: (ruleForm.splits || []).filter((s) => s.pool && s.percent).map((s) => ({ pool: s.pool, percent: parseFloat(s.percent) })),
      };
      if (ruleForm.id) {
        await adminAPI.updateRevenueRule(ruleForm.id, payload);
      } else {
        await adminAPI.createRevenueRule({ source: ruleForm.source, ...payload });
      }
      toast.success('Saved');
      setRuleForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const deleteRule = async (r) => {
    if (!window.confirm(`Delete rule for "${r.source}"? Revenue from this source will default to 100% Profit.`)) return;
    try {
      await adminAPI.deleteRevenueRule(r.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const addSplit = () => setRuleForm({ ...ruleForm, splits: [...(ruleForm.splits || []), { pool: '', percent: 0 }] });
  const updateSplit = (i, patch) => {
    const splits = [...(ruleForm.splits || [])];
    splits[i] = { ...splits[i], ...patch };
    setRuleForm({ ...ruleForm, splits });
  };
  const removeSplit = (i) => {
    const splits = [...(ruleForm.splits || [])];
    splits.splice(i, 1);
    setRuleForm({ ...ruleForm, splits });
  };

  if (!overview) {
    return <AdminLayout><div className="p-8 text-center">Loading…</div></AdminLayout>;
  }

  const pools = overview.pools || [];
  const currenciesInUse = new Set([
    ...Object.keys(overview.totals_by_currency || {}),
    ...Object.keys(overview.user_liabilities_by_currency || {}),
  ]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-treasury-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Treasury</h1>
            <p className="mt-2 text-muted-foreground">Multi-currency pools, revenue routing, and platform obligations.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
            <Button variant="outline" onClick={() => setTransferForm({ from_pool: '', to_pool: '', currency: 'ZAR', amount: '', reason: '' })} data-testid="transfer-btn"><ArrowRightLeft className="h-4 w-4 mr-2" /> Transfer</Button>
          </div>
        </div>

        {/* Health overview */}
        {[...currenciesInUse].length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Solvency check</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-3">
                {[...currenciesInUse].map((cur) => {
                  const treasury = overview.totals_by_currency[cur] || 0;
                  const liability = overview.user_liabilities_by_currency[cur] || 0;
                  const solvent = treasury >= liability;
                  return (
                    <div key={cur} className="p-3 rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{cur}</p>
                        {solvent ? <Badge className="bg-emerald-600">Solvent</Badge> : <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" /> Below</Badge>}
                      </div>
                      <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Treasury pools</p>
                          <p className="font-mono">{treasury.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">User liabilities</p>
                          <p className="font-mono">{liability.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Note: Phase 1 treasury pools start empty — this dashboard becomes meaningful once revenue starts flowing. Solvency will formally include on-chain treasury custody in Phase 3.
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="pools">
          <TabsList>
            <TabsTrigger value="pools" data-testid="tab-pools">Pools ({pools.length})</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">Revenue rules ({rules.length})</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger ({ledger.length})</TabsTrigger>
          </TabsList>

          {/* POOLS */}
          <TabsContent value="pools" className="mt-6">
            <div className="flex justify-end mb-3">
              <Button size="sm" onClick={() => setPoolForm({ key: '', name: '', description: '', enabled: true })} data-testid="new-pool-btn"><Plus className="h-4 w-4 mr-1" /> Custom pool</Button>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {pools.map((p) => (
                <Card key={p.id} data-testid={`pool-${p.key}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base truncate">{p.name}</CardTitle>
                          {p.protected && <Badge variant="outline" className="text-[10px]">Protected</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {Object.keys(p.balances || {}).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">Empty</p>
                    ) : (
                      <div className="space-y-1">
                        {Object.entries(p.balances).sort(([a], [b]) => a.localeCompare(b)).map(([cur, val]) => (
                          <div key={cur} className="text-xs flex justify-between font-mono">
                            <span>{cur}</span>
                            <span className={val < 0 ? 'text-red-500' : 'text-foreground'}>{Number(val).toLocaleString(undefined, { maximumFractionDigits: 8 })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-1 mt-3">
                      <Button size="sm" variant="ghost" onClick={() => setPoolForm({ ...p })} data-testid={`edit-pool-${p.key}`}>Edit</Button>
                      {!p.protected && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deletePool(p)} data-testid={`delete-pool-${p.key}`}><Trash2 className="h-3 w-3" /></Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* RULES */}
          <TabsContent value="rules" className="mt-6">
            <div className="flex justify-end mb-3">
              <Button size="sm" onClick={() => setRuleForm({ source: '', label: '', enabled: true, splits: [] })} data-testid="new-rule-btn"><Plus className="h-4 w-4 mr-1" /> New rule</Button>
            </div>
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>Splits</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((r) => (
                      <TableRow key={r.id} data-testid={`rule-${r.source}`}>
                        <TableCell><code className="text-xs">{r.source}</code></TableCell>
                        <TableCell>{r.label}</TableCell>
                        <TableCell className="text-xs">
                          {(r.splits || []).map((s, i) => <span key={i} className="mr-2">{s.percent}% → {s.pool}</span>)}
                        </TableCell>
                        <TableCell>{r.enabled ? <Badge className="bg-emerald-600">On</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => setRuleForm({ ...r })}>Edit</Button>
                          <Button size="icon" variant="ghost" className="text-red-500" onClick={() => deleteRule(r)}><Trash2 className="h-3 w-3" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* REVENUE aggregates */}
          <TabsContent value="revenue" className="mt-6">
            <div className="grid gap-3 md:grid-cols-2">
              <RevenueCard title="Last 24 hours" rows={overview.revenue_24h} />
              <RevenueCard title="Last 30 days" rows={overview.revenue_30d} />
            </div>
          </TabsContent>

          {/* LEDGER */}
          <TabsContent value="ledger" className="mt-6">
            <Card>
              <CardContent className="pt-6">
                {ledger.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No treasury entries yet. Trigger a swap or a tasking pack purchase to see the routing engine at work.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Pool</TableHead>
                        <TableHead>Direction</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ledger.map((l) => (
                        <TableRow key={l.id} data-testid={`ledger-${l.id}`}>
                          <TableCell className="text-xs">{formatDate(l.created_at)}</TableCell>
                          <TableCell><code className="text-xs">{l.pool}</code></TableCell>
                          <TableCell>
                            <Badge className={l.direction === 'credit' ? 'bg-emerald-600' : 'bg-amber-600'}>{l.direction}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{l.amount} {l.currency}</TableCell>
                          <TableCell className="text-xs">{l.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Pool dialog */}
      <Dialog open={!!poolForm} onOpenChange={(v) => !v && setPoolForm(null)}>
        <DialogContent data-testid="pool-dialog">
          <DialogHeader><DialogTitle>{poolForm?.id ? 'Edit pool' : 'New pool'}</DialogTitle></DialogHeader>
          {poolForm && (
            <div className="space-y-3">
              {!poolForm.id && (
                <div><Label>Key (lowercase, no spaces)</Label>
                  <Input value={poolForm.key} onChange={(e) => setPoolForm({ ...poolForm, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="marketing" data-testid="pool-key-input" />
                </div>
              )}
              <div><Label>Name</Label>
                <Input value={poolForm.name} onChange={(e) => setPoolForm({ ...poolForm, name: e.target.value })} data-testid="pool-name-input" />
              </div>
              <div><Label>Description</Label>
                <Textarea rows={2} value={poolForm.description || ''} onChange={(e) => setPoolForm({ ...poolForm, description: e.target.value })} />
              </div>
              {poolForm.id && (
                <div className="flex items-center gap-2">
                  <Switch checked={poolForm.enabled} onCheckedChange={(v) => setPoolForm({ ...poolForm, enabled: v })} />
                  <Label>Enabled</Label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoolForm(null)}>Cancel</Button>
            <Button onClick={savePool} data-testid="save-pool-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={!!transferForm} onOpenChange={(v) => !v && setTransferForm(null)}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer between pools</DialogTitle></DialogHeader>
          {transferForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>From pool</Label>
                  <Select value={transferForm.from_pool} onValueChange={(v) => setTransferForm({ ...transferForm, from_pool: v })}>
                    <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source" /></SelectTrigger>
                    <SelectContent>{pools.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>To pool</Label>
                  <Select value={transferForm.to_pool} onValueChange={(v) => setTransferForm({ ...transferForm, to_pool: v })}>
                    <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
                    <SelectContent>{pools.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Currency</Label>
                  <Input value={transferForm.currency} onChange={(e) => setTransferForm({ ...transferForm, currency: e.target.value.toUpperCase() })} data-testid="transfer-currency" /></div>
                <div><Label>Amount</Label>
                  <Input type="number" value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} data-testid="transfer-amount" /></div>
              </div>
              <div><Label>Reason</Label>
                <Input value={transferForm.reason} onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })} placeholder="Reason for the transfer" data-testid="transfer-reason" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferForm(null)}>Cancel</Button>
            <Button onClick={doTransfer} data-testid="confirm-transfer-btn"><ArrowRightLeft className="h-4 w-4 mr-2" /> Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule dialog */}
      <Dialog open={!!ruleForm} onOpenChange={(v) => !v && setRuleForm(null)}>
        <DialogContent className="max-w-lg" data-testid="rule-dialog">
          <DialogHeader><DialogTitle>{ruleForm?.id ? 'Edit rule' : 'New revenue routing rule'}</DialogTitle></DialogHeader>
          {ruleForm && (
            <div className="space-y-3">
              {!ruleForm.id && (
                <div><Label>Source (lowercase key)</Label>
                  <Input value={ruleForm.source} onChange={(e) => setRuleForm({ ...ruleForm, source: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="e.g. trading_fee" data-testid="rule-source-input" /></div>
              )}
              <div><Label>Label</Label>
                <Input value={ruleForm.label} onChange={(e) => setRuleForm({ ...ruleForm, label: e.target.value })} /></div>
              <div className="flex items-center gap-2"><Switch checked={ruleForm.enabled} onCheckedChange={(v) => setRuleForm({ ...ruleForm, enabled: v })} /><Label>Enabled</Label></div>

              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Splits (sum ≤ 100%; remainder → last row)</p>
                  <Button size="sm" variant="ghost" onClick={addSplit} data-testid="add-split-btn"><Plus className="h-3 w-3 mr-1" /> Add split</Button>
                </div>
                {(ruleForm.splits || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_40px] gap-2 mb-2">
                    <Select value={s.pool} onValueChange={(v) => updateSplit(i, { pool: v })}>
                      <SelectTrigger data-testid={`split-pool-${i}`}><SelectValue placeholder="Pool" /></SelectTrigger>
                      <SelectContent>{pools.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" step="0.01" value={s.percent} onChange={(e) => updateSplit(i, { percent: e.target.value })} placeholder="%" data-testid={`split-percent-${i}`} />
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeSplit(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground mt-2">
                  Total: {(ruleForm.splits || []).reduce((sum, s) => sum + (parseFloat(s.percent) || 0), 0)}%
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleForm(null)}>Cancel</Button>
            <Button onClick={saveRule} data-testid="save-rule-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

const RevenueCard = ({ title, rows }) => (
  <Card>
    <CardHeader className="pb-3">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-500" />
        <CardTitle className="text-base">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      {!rows || rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No revenue in this window.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Source</TableHead><TableHead>Currency</TableHead><TableHead className="text-right">Total</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs"><code>{r.source}</code></TableCell>
                <TableCell>{r.currency}</TableCell>
                <TableCell className="text-right font-mono">{Number(r.total).toLocaleString(undefined, { maximumFractionDigits: 8 })}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default AdminTreasuryPage;
